// Reference Library screens. Pure: data in, HTML out.
// Words: REFERENCE_LIBRARY_WORDS.md. Mockup: MOCKUP_REFERENCE_LIBRARY_APPROVED.html (approved 2026-10-08).
// Every change goes through server code (functions/src/references.ts). Files upload to Storage first.

import type { ReferencesData } from "./firebase";
import {
  ACCEPT,
  COPY_JOB_URL,
  KINDS,
  KIND_GROUP,
  KIND_SHORT,
  LIVE_SOURCES_LINE,
  formReady,
  copyLineOf,
  fileTypeText,
  groupRefs,
  historyText,
  nextRefId,
  shortName,
  sizeText,
  type FileInfo,
  type KindGroup,
  type Reference,
  type RefInput,
} from "./references";
import { shortDay } from "./tasks";
import { esc, ident } from "./training-screens";

function row(label: string, value: string, end = ""): string {
  return `<div class="r"><div class="k">${esc(label)}</div><div class="v">${value}</div><div class="e">${end}</div></div>`;
}

function sheet(title: string, body: string): string {
  return `<div class="shade"><div class="sheet" role="dialog" aria-modal="true" aria-label="${esc(title)}">
    <div class="big">${esc(title)}</div>${body}</div></div>`;
}

const NONE = '<span class="muted">None</span>';
const LOCK = '<span class="lock">Locked</span>';

function statusPill(r: Reference): string {
  return `<span class="pill ${r.status === "Current" ? "green" : "grey"}">${r.status.toUpperCase()}</span>`;
}

function textPill(r: Reference): string {
  return r.hasText === false ? '<span class="pill amber">NO SEARCHABLE TEXT</span>' : "";
}

function newestAdded(d: ReferencesData): string {
  return d.refs.reduce((m, r) => (r.addedOn > m ? r.addedOn : m), "");
}

// ---- 2. The list ----

export function refsShell(d: ReferencesData, search: string, flash = ""): string {
  const current = d.refs.filter((r) => r.status === "Current").length;
  const superseded = d.refs.length - current;
  const line = copyLineOf(d.copy, newestAdded(d));
  return `${ident("RL", "Reference Library", "Standards, manuals and studies the SOP drafter reads")}
    ${flash}
    <div class="note ${line.tone}">${esc(line.text)} <a class="lnk" href="${COPY_JOB_URL}" target="_blank" rel="noopener">Copy Now</a></div>
    <div class="box"><div class="cnt">
      <div class="cnt-cell"><b>${current}</b><small>CURRENT</small></div>
      <div class="cnt-cell"><b>${superseded}</b><small>SUPERSEDED</small></div>
      <a class="cnt-cell tk-add" href="#/references/new"><b>+</b><small>ADD</small></a>
    </div></div>
    <label class="search"><span class="sr">Search</span><input id="ref-search" class="fin wide" type="search" value="${esc(search)}" placeholder="Search title, number or edition" autocomplete="off" autocapitalize="off" spellcheck="false"></label>
    <div id="ref-groups"></div>`;
}

function refRow(r: Reference): string {
  const sub = [r.edition, r.status === "Superseded" && r.replacedBy ? `Replaced by ${r.replacedBy}` : r.covers].filter(Boolean).join(" · ");
  return `<a class="tk-row${r.status === "Current" ? "" : " dim"}" href="#/references/r/${encodeURIComponent(r.refId)}">
    <span class="t"><b>${esc(r.number ? `${r.number} · ${r.title.replace(new RegExp(`^${r.number.replace(/[.*+?^${}()|[\]\\]/g, "\\$&")},?\\s*`), "")}` : r.title)}</b>${sub ? `<small class="muted rl-sub">${esc(sub)}</small>` : ""}
    <span class="tk-tags"><span class="chip">${esc(r.refId)}</span>${statusPill(r)}${textPill(r)}</span></span>
    <span class="chev">›</span></a>`;
}

export function refGroups(groups: KindGroup[], search: string): string {
  const live = `<div class="note grey">${esc(LIVE_SOURCES_LINE)}</div>`;
  if (!groups.length) {
    return search.trim()
      ? `<div class="box"><div class="empty">No reference matches "${esc(search)}".</div></div>${live}`
      : `<div class="box"><div class="empty">The library is empty. Add your first reference: your NFPA 70E copy is a good start.</div></div>${live}`;
  }
  const shown = groups.filter((g) => g.refs.length);
  const body = shown
    .map(
      (g) => `<div class="grp"><span>${esc(KIND_GROUP[g.kind])}</span><span>${g.current} current${g.superseded ? ` · ${g.superseded} superseded` : ""}</span></div>
      <div class="box">${g.refs.map(refRow).join("")}</div>${g.kind === "Standard" ? live : ""}`,
    )
    .join("");
  return (body || `<div class="box"><div class="empty">No reference matches "${esc(search)}".</div></div>`) + (shown.some((g) => g.kind === "Standard") ? "" : live);
}

export function groupsHtml(d: ReferencesData, search: string): string {
  return refGroups(groupRefs(d.refs, search), search);
}

// ---- 6. Open a reference ----

export function refDetail(r: Reference, d: ReferencesData, flash = ""): string {
  const id = encodeURIComponent(r.refId);
  const isCurrent = r.status === "Current";
  const fileLine = `<span class="mono">${esc(r.fileName || "No file")}</span> <small class="muted">${esc([fileTypeText(r.contentType), sizeText(r.sizeBytes), r.hasText === null ? "copy for Claude pending" : r.hasText ? "searchable" : "no searchable text"].filter(Boolean).join(" · "))}</small>`;
  const link = (refId: string) => `<a class="lnk" href="#/references/r/${encodeURIComponent(refId)}">${esc(refId)}</a>`;
  const hist = r.history.length
    ? [...r.history]
        .sort((a, b) => b.at.localeCompare(a.at))
        .map(
          (h) => `<div class="tk-hist"><b>${esc(shortDay(h.at))} · ${esc(historyText(h))}</b><small>${esc(h.byName || h.by)}${h.reason ? ` · ${esc(h.reason)}` : ""}${h.fileName ? ` · ${esc(h.action === "file replaced" ? `old file ${h.fileName}` : h.fileName)}` : ""}</small>${
            h.action === "file replaced" && h.storagePath ? `<button class="chg sm" type="button" data-act="ref-open" data-path="${esc(h.storagePath)}">Open Old File</button>` : ""
          }</div>`,
        )
        .join("")
    : '<div class="empty">No history yet.</div>';
  const chg = (field: string) => (isCurrent ? `<a class="chg" href="#/references/r/${id}/edit" data-field="${field}">Change</a>` : "");
  return `<a class="back" href="#/references">Reference Library</a>
    ${ident(r.refId.replace("REF-", ""), shortName(r), `${r.kind} · ${r.status}`)}
    ${flash}
    ${!isCurrent && r.replacedBy ? `<div class="note grey">Superseded. Replaced by ${link(r.replacedBy)}. The drafter treats the newer edition as current.</div>` : ""}
    <div class="box">
      <div class="boxhd"><div class="nm"><b>${esc(r.title)}</b><small>The ID, kind, number, edition and file are locked. A new edition is its own record.</small></div>${statusPill(r)}</div>
      <div class="rows">
        ${row("ID", `<span class="chip">${esc(r.refId)}</span>`, LOCK)}
        ${row("Kind", esc(r.kind), LOCK)}
        ${row("Title", esc(r.title), chg("title"))}
        ${row("Number", r.number ? esc(r.number) : NONE, LOCK)}
        ${row("Edition", r.edition ? esc(r.edition) : NONE, LOCK)}
        ${row("Covers", r.covers ? esc(r.covers) : NONE, chg("covers"))}
        ${row("Notes", r.notes ? esc(r.notes) : NONE, chg("notes"))}
        ${r.replaces ? row("Replaces", link(r.replaces)) : ""}
        ${row("File", fileLine)}
        ${row("Added", `${esc(shortDay(r.addedOn))} <small class="muted">${esc(r.addedByName)}</small>`)}
      </div>
    </div>
    <div class="pb-actions">
      <button class="prim" type="button" data-act="ref-open" data-path="${esc(r.storagePath)}"${r.storagePath ? "" : " disabled"}>Open File</button>
      ${isCurrent ? `<a class="chg" href="#/references/r/${id}/replace">Replace File</a><a class="chg" href="#/references/r/${id}/edition">Add New Edition</a><button class="chg" type="button" data-act="ref-retire">Retire</button>` : ""}
    </div>
    <div class="box">
      <div class="boxhd"><div class="nm"><b>History</b><small>Added, changed, superseded and retired, with who and why</small></div></div>
      ${hist}
    </div>`;
}

// ---- 3 to 5. Add Reference, Add New Edition, Replace File ----

export type FormMode = "new" | "edition" | "replace";

function fileBox(file: FileInfo | null, mode: FormMode): string {
  const picked = file ? `<span class="rl-file"><b class="mono">${esc(file.name)}</b><small>${esc([fileTypeText(file.type), sizeText(file.size)].filter(Boolean).join(" · "))}</small></span>` : "";
  return `<label class="r"><span class="k">File (required)</span><span class="v">
    ${picked}
    <span class="rl-pick"><input id="ref-file" type="file" accept="${ACCEPT}"><span class="chg">${file ? "Choose A Different File" : mode === "replace" ? "Choose The New File" : "Choose File"}</span></span>
    <small class="fhint">PDF, Word or text. Up to 50 MB.</small></span><span class="e"></span></label>`;
}

export function refForm(mode: FormMode, d: ReferencesData, base: Reference | null, entry: RefInput, file: FileInfo | null, busy: string, error = ""): string {
  const nextId = nextRefId(d.refs.map((r) => r.refId));
  const lockedKind = mode !== "new";
  const seg = KINDS.map(
    (k) => `<label class="seg-opt"><input type="radio" name="kind" value="${esc(k)}"${entry.kind === k ? " checked" : ""}${lockedKind ? " disabled" : ""}><span>${esc(KIND_SHORT[k])}</span></label>`,
  ).join("");
  const input = (name: keyof RefInput, label: string, ph: string, opts = "") =>
    `<label class="r"><span class="k">${esc(label)}</span><span class="v"><input class="fin wide" name="${name}" value="${esc(entry[name])}" placeholder="${esc(ph)}" autocomplete="off"${opts}></span><span class="e"></span></label>`;
  const title = mode === "new" ? "Add Reference" : mode === "edition" ? "Add New Edition" : "Replace File";
  const sub = mode === "new" ? "For a standard, manual or study the program rests on" : `${base ? shortName(base) : ""} · ${base?.refId ?? ""}`;
  const head = mode === "replace" ? `Same edition, better copy. The old file stays in History.` : `The next free ID fills in.`;
  const cancel = mode === "new" ? "#/references" : `#/references/r/${encodeURIComponent(base?.refId ?? "")}`;
  const ready = formReady(mode, entry, file) && !busy;
  const fields =
    mode === "replace"
      ? input("covers", "Covers", "e.g. Full standard")
      : `<div class="r"><span class="k">Kind (required)</span><span class="v"><div class="seg kinds" role="radiogroup" aria-label="Kind">${seg}</div>${lockedKind ? '<small class="fhint">A new edition keeps the kind and number.</small>' : ""}</span><span class="e"></span></div>
        ${input("title", "Title (required)", "e.g. NFPA 70B, Electrical Equipment Maintenance", ' maxlength="140"')}
        ${input("number", "Number", "e.g. NFPA 70B", ` maxlength="60"${lockedKind ? " readonly" : ""}`)}
        ${input("edition", "Edition", "e.g. 2023", ' maxlength="30"')}
        ${input("covers", "Covers", "e.g. Full standard, or Chapter 2 only", ' maxlength="120"')}
        ${input("notes", "Notes (optional)", "", ' maxlength="500"')}`;
  return `<a class="back" href="${cancel}">Cancel</a>
    ${ident("+", title, sub)}
    <form class="box" id="ref-form" data-mode="${mode}" data-ref="${esc(base?.refId ?? "")}" novalidate>
      <div class="boxhd"><div class="nm"><b>${mode === "replace" ? esc(base?.refId ?? "") : "New Reference"}</b><small>${esc(head)}</small></div>${mode === "replace" ? "" : `<span class="chip">${esc(nextId)}</span>`}</div>
      <div class="rows">
        ${fileBox(file, mode)}
        ${fields}
      </div>
    </form>
    <div class="note${ready ? " green" : ""}" id="ref-form-note">${esc(formNote(mode, entry, file, ready))}</div>
    ${error ? `<div class="note red">${esc(error)}</div>` : ""}
    <button class="prim" type="submit" form="ref-form" id="ref-save"${ready ? "" : " disabled"}>${busy ? esc(busy) : mode === "replace" ? "Replace File" : mode === "edition" ? "Add New Edition" : "Save"}</button>`;
}

export function formNote(mode: FormMode, entry: RefInput, file: FileInfo | null, ready: boolean): string {
  if (ready) return mode === "replace" ? "Ready. The record points to the new file. The old file stays in History." : "Ready. The drafter can read it after the next copy for Claude.";
  if (mode === "replace") return "Choose the new file.";
  return entry.kind === "Standard"
    ? "Save stays off until the file, title and edition are in."
    : "Kind has no default. Save stays off until the file, kind and title are in, and the edition for a standard.";
}

/** Title, covers and notes. */
export function refEditForm(r: Reference, entry: { title: string; covers: string; notes: string } | null, busy: boolean, error = ""): string {
  const v = entry ?? { title: r.title, covers: r.covers, notes: r.notes };
  return `<a class="back" href="#/references/r/${encodeURIComponent(r.refId)}">Cancel</a>
    ${ident(r.refId.replace("REF-", ""), "Change Reference", `${shortName(r)} · ${r.refId}`)}
    <form class="box" id="ref-edit-form" data-ref="${esc(r.refId)}" novalidate>
      <div class="rows">
        <label class="r"><span class="k">Title</span><span class="v"><input class="fin wide" name="title" value="${esc(v.title)}" maxlength="140" autocomplete="off"></span><span class="e"></span></label>
        <label class="r"><span class="k">Covers</span><span class="v"><input class="fin wide" name="covers" value="${esc(v.covers)}" maxlength="120" autocomplete="off"></span><span class="e"></span></label>
        <label class="r"><span class="k">Notes</span><span class="v"><input class="fin wide" name="notes" value="${esc(v.notes)}" maxlength="500" autocomplete="off"></span><span class="e"></span></label>
      </div>
    </form>
    ${error ? `<div class="note red">${esc(error)}</div>` : ""}
    <button class="prim" type="submit" form="ref-edit-form"${busy ? " disabled" : ""}>${busy ? "Saving…" : "Save"}</button>`;
}

// ---- 7 and 8. Confirms ----

export function editionSheet(old: Reference, entry: RefInput, nextId: string, busy: string, error = ""): string {
  const name = `${entry.number || old.number} ${entry.edition}`.trim();
  return sheet(
    `Add ${name}?`,
    `<p>${esc(nextId)} becomes the current ${esc(old.number || old.title)}. ${esc(old.refId)} (${esc(old.edition)}) turns Superseded and keeps its file. SOPs keep citing ${esc(old.edition)} until each one is revised. Ask Claude to run the update for ${esc(name)} to see which SOPs it touches.</p>
    ${error ? `<div class="note red">${esc(error)}</div>` : ""}
    <button class="go" type="button" data-act="ref-edition-confirm"${busy ? " disabled" : ""}>${busy ? esc(busy) : "Add New Edition"}</button>
    <button class="go2" type="button" data-act="sheet-cancel">Cancel</button>`,
  );
}

export function retireSheet(r: Reference, reason: string, busy: boolean, error = ""): string {
  return sheet(
    "Retire this reference?",
    `<p>${esc(shortName(r))} turns Superseded and stays in the library with its file. The drafter stops treating it as current. Nothing is deleted.</p>
    <div class="sheet-card"><label class="fl"><span class="lbl">Reason (required)</span><input id="ref-reason" maxlength="200" value="${esc(reason)}" placeholder="Why it is no longer used" autocomplete="off"></label></div>
    ${error ? `<div class="note red">${esc(error)}</div>` : ""}
    <button class="go red" type="button" data-act="ref-retire-confirm" data-needs-reason${reason.trim() && !busy ? "" : " disabled"}>${busy ? "Saving…" : "Retire"}</button>
    <button class="go2" type="button" data-act="sheet-cancel">Cancel</button>`,
  );
}
